import { useEffect, useState } from 'preact/hooks';
import { request, type Session } from '../api/client';
import { displayDate } from '../api/adapter';
import type { WireReflection, WireTracker } from '../api/wire';

import { SectionHeading } from '../components/composed/SectionHeading';
import { TrackerChip } from '../components/trackers/TrackerChip.jsx';
import { EmptyState } from '../components/feedback/EmptyState.jsx';

/** The closing ritual as a band across the spread.
 *
 *  Above the first breakpoint the reflection and trackers leave the day column
 *  and become one band underneath both pages — the day is what differs between
 *  the pages, and the closing ritual belongs to the day you are actually
 *  closing, not to each column.
 *
 *  It fetches its own reflection and tracker rather than being handed them,
 *  because the pages fetch independently and threading one page's data into a
 *  shared band would tie the band to whichever page happened to load first.
 */
export function RitualBand({
  session,
  journalDate,
  onEditReflection,
  refresh,
}: {
  session: Session;
  journalDate: string;
  onEditReflection: () => void;
  /** Bumped when something on this device changed the journal. A signal, not a
   *  key — see the note in `Today`. */
  refresh?: number;
}) {
  const [reflection, setReflection] = useState<WireReflection | null>(null);
  const [tracker, setTracker] = useState<WireTracker | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [r, t] = await Promise.all([
        request<WireReflection>(session, `/v1/days/${journalDate}/reflection`)
          .then((res) => res.body)
          .catch(() => null),
        request<WireTracker>(
          session,
          `/v1/habits/tracker?from=${journalDate}&to=${journalDate}`,
        )
          .then((res) => res.body)
          .catch(() => null),
      ]);
      if (cancelled) return;
      setReflection(r);
      setTracker(t);
    })();
    return () => {
      cancelled = true;
    };
  }, [session, journalDate, refresh]);

  const habits = (tracker?.items ?? []).map((habit) => ({
    id: habit.id,
    name: habit.name,
    goal: habit.goal_per_day || 1,
    count: habit.days[0]?.count ?? 0,
  }));

  return (
    <div style={band}>
      <section style={{ flex: 1, minWidth: 0 }}>
        <SectionHeading muted note={`${displayDate(journalDate)} · ${reflection?.content ? 'TAP TO EDIT' : 'TAP TO WRITE'}`}>
          REFLECTION
        </SectionHeading>
        <button type="button" onClick={onEditReflection} style={reflectionButton}>
          {reflection?.content ? (
            <span style={prose}>{reflection.content}</span>
          ) : (
            <span style={{ ...prose, color: 'var(--bujo-text-meta)' }}>Not written.</span>
          )}
        </button>
      </section>

      <section style={{ flex: '0 0 40%', minWidth: 0 }}>
        <SectionHeading muted
          note={`${habits.length} HABIT${habits.length === 1 ? '' : 'S'} · ${habits.filter((h) => h.count > 0).length} LOGGED`}
        >
          TRACKERS
        </SectionHeading>
        {habits.length === 0 ? (
          <EmptyState line="No habits yet." />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--bujo-space-2)', paddingTop: 'var(--bujo-space-2)' }}>
            {habits.map((habit) => (
              <TrackerChip key={habit.id} name={habit.name} done={habit.count} goal={habit.goal} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const band = {
  display: 'flex',
  gap: 'var(--bujo-space-5)',
  alignItems: 'flex-start',
  flexWrap: 'wrap' as const,
  padding: 'var(--bujo-space-4) var(--bujo-gutter)',
  borderTop: '1px solid var(--bujo-rule-heavy)',
};
const reflectionButton = {
  display: 'block',
  width: '100%',
  textAlign: 'left' as const,
  minHeight: 'var(--bujo-row-min)',
  background: 'none',
  border: 0,
  padding: 0,
  cursor: 'pointer',
};
const prose = {
  font: 'var(--bujo-fw-regular) 15px/var(--bujo-lh-prose) var(--bujo-font-sans)',
  color: 'var(--bujo-text-primary)',
};
